import { inject, observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import withAuth from '../../../HOC/withAuth';
import InfoBar from '../InfoBar';
import { passwordRegex } from '../../../const';
import C from '../../../colors';

function PrimaryBtn({ children, onClick, type = 'button' }) {
  return (
    <button type={type} onClick={onClick} style={{
      fontFamily: C.sans, fontSize: 13, fontWeight: 500,
      padding: '9px 16px', borderRadius: 7,
      background: C.brand, color: '#fff', border: 'none', cursor: 'pointer',
      boxShadow: `0 1px 0 ${C.brand2}, 0 6px 16px -8px rgba(217,79,146,.55)`,
      whiteSpace: 'nowrap',
    }}>{children}</button>
  );
}

function GhostBtn({ children, onClick }) {
  return (
    <button onClick={onClick} style={{
      fontFamily: C.sans, fontSize: 13, fontWeight: 500,
      padding: '8px 14px', borderRadius: 7,
      background: '#fff', color: C.ink2,
      border: `1px solid ${C.hair2}`, cursor: 'pointer',
      whiteSpace: 'nowrap',
    }}>{children}</button>
  );
}

function SectionLabel({ children }) {
  return (
    <div style={{
      fontFamily: C.mono, fontSize: 10, letterSpacing: 1.4,
      textTransform: 'uppercase', color: C.ink3, fontWeight: 600,
      marginBottom: 12,
    }}>{children}</div>
  );
}

function Toggle({ checked, onChange, disabled, label, testId }) {
  return (
    <button
      type="button"
      role="switch"
      aria-label={label}
      aria-checked={checked}
      data-testid={testId}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      style={{
        position: 'relative',
        width: 44, height: 26,
        borderRadius: 13,
        border: 'none',
        background: disabled ? C.hair2 : checked ? C.brand : C.hair2,
        cursor: disabled ? 'not-allowed' : 'pointer',
        padding: 0,
        flexShrink: 0,
        transition: 'background .2s',
      }}
    >
      <span style={{
        position: 'absolute',
        top: 3,
        left: checked ? 21 : 3,
        width: 20, height: 20,
        borderRadius: 10,
        background: '#fff',
        boxShadow: '0 1px 3px rgba(0,0,0,.2)',
        transition: 'left .2s',
      }}/>
    </button>
  );
}

const SmsNotificationCard = inject('userStore', 'snackbarStore', 'chargeStore')(observer(({ userStore, snackbarStore, chargeStore }) => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    userStore.fetchNotificationSettings()
      .then(data => setSettings(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [userStore]);

  const handleToggle = (next) => {
    const eligible = typeof settings?.smsEligible === 'boolean'
      ? settings.smsEligible
      : Number(settings?.currentLevel ?? userStore.level) >= 2 && settings?.subscriptionActive !== false;
    if (saving || !eligible) return;
    const prev = settings.smsSelectionNotificationEnabled;
    setSettings(s => ({ ...s, smsSelectionNotificationEnabled: next }));
    setSaving(true);
    userStore.updateNotificationSettings({ smsSelectionNotificationEnabled: next })
      .then(() => {
        snackbarStore.showSnackbar(next ? '短信通知已开启' : '短信通知已关闭');
      })
      .catch(err => {
        setSettings(s => ({ ...s, smsSelectionNotificationEnabled: prev }));
        if (err?.response?.status === 403) {
          snackbarStore.showSnackbar('短信选片通知为高级会员功能，请升级后使用', 'error');
        } else {
          snackbarStore.showSnackbar('保存失败，请稍后重试', 'error');
        }
      })
      .finally(() => setSaving(false));
  };

  if (loading) return null;
  if (!settings) return null;

  const { smsSelectionNotificationEnabled, effectiveSmsPhone } = settings;
  const smsEligible = typeof settings.smsEligible === 'boolean'
    ? settings.smsEligible
    : Number(settings.currentLevel ?? userStore.level) >= 2 && settings.subscriptionActive !== false;

  return (
    <div style={{
      background: '#fff', border: `1px solid ${C.hair}`, borderRadius: 14,
      padding: '20px 22px',
    }}>
      <SectionLabel>通知设置</SectionLabel>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 500, color: C.ink, marginBottom: 4 }}>
            短信选片通知
          </div>
          <div style={{ fontSize: 12, color: C.ink3, lineHeight: 1.6, marginBottom: 8 }}>
            客户提交选片后，通过短信提醒你。
          </div>
          {effectiveSmsPhone && (
            <div style={{
              display: 'inline-flex', alignItems: 'center',
              fontSize: 11, color: C.ink3,
              background: C.paper2, border: `1px solid ${C.hair}`,
              borderRadius: 6, padding: '3px 8px',
              fontFamily: C.mono,
            }}>
              通知手机号：{effectiveSmsPhone}
            </div>
          )}
          <div style={{ marginTop: 10 }}>
            <GhostBtn onClick={() => navigate('/user/info/sms')}>查看发送记录</GhostBtn>
          </div>
          {!smsEligible && (
            <div style={{
              marginTop: 10,
              background: C.paper2, border: `1px solid ${C.hair}`,
              borderRadius: 8, padding: '10px 12px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 12, flexWrap: 'wrap',
            }}>
              <div style={{ fontSize: 12, color: C.ink2 }}>
                高级会员功能，开通高级会员后可用。
              </div>
              <button
                onClick={() => chargeStore.openChargeDialog(false)}
                style={{
                  fontFamily: C.sans, fontSize: 12, fontWeight: 500,
                  padding: '5px 12px', borderRadius: 6, flexShrink: 0,
                  background: C.ink, color: '#fff', border: 'none', cursor: 'pointer',
                }}
              >
                升级会员
              </button>
            </div>
          )}
        </div>
        <Toggle
          checked={!!smsSelectionNotificationEnabled}
          onChange={handleToggle}
          disabled={!smsEligible || saving}
          label="短信选片通知"
          testId="sms-selection-notification-toggle"
        />
      </div>
    </div>
  );
}));

const UserInfoPage = inject('userStore', 'snackbarStore')(observer(({ userStore, snackbarStore }) => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState(false);
  const [username, setUsername] = useState('');
  const [userNameError, setUserNameError] = useState(false);

  const validatePassword = (v) => {
    if (!v) return '密码不能为空';
    if (!passwordRegex.test(v)) return '密码应至少为6个字符，最多16个字符，可以包含字母（无论大小写）、数字以及特殊字符(@$!%*#?&)。';
    return '';
  };
  const validateConfirmPassword = (v) => {
    if (!v) return '密码不能为空';
    if (v !== password) return '两次输入的密码不一致！';
    return '';
  };
  const validateUserName = (v) => {
    if (!v) return 'Username can not be empty';
    return '';
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    if (name === 'confirmPassword') { setConfirmPassword(value); setConfirmPasswordError(validateConfirmPassword(value)); }
    else if (name === 'password') { setPassword(value); setPasswordError(validatePassword(value)); }
    else if (name === 'username') { setUsername(value); setUserNameError(validateUserName(value)); }
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    const pe = validatePassword(password);
    const ce = validateConfirmPassword(confirmPassword);
    setPasswordError(pe); setConfirmPasswordError(ce);
    if (!pe && !ce) {
      userStore.changePassword(password).then(() => {
        snackbarStore.showSnackbar('密码修改成功');
        userStore.logout();
      });
    }
  };

  const handleUserNameSubmit = (e) => {
    e.preventDefault();
    const ue = validateUserName(username);
    setUserNameError(ue);
    if (!ue) {
      userStore.changeUsername(username).then(() => {
        snackbarStore.showSnackbar('用户名修改成功');
        userStore.username = username;
      });
    }
  };

  useEffect(() => { setUsername(userStore.username); }, [userStore.username]);

  const inputStyle = (hasError) => ({
    width: '100%', padding: '10px 14px', boxSizing: 'border-box',
    border: `1px solid ${hasError ? '#e88c7c' : C.hair2}`, borderRadius: 8,
    fontSize: 14, fontFamily: C.sans, outline: 'none', color: C.ink,
    background: '#fff',
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <InfoBar />
      <div style={{ flex: 1, overflow: 'auto' }}>
        <div style={{ maxWidth: 960, margin: '0 auto', padding: '32px 32px 64px' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '380px 1fr',
            gap: 20,
            alignItems: 'start',
          }}>

            {/* LEFT: account overview */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

              {/* Balance */}
              <div style={{
                background: '#fff', border: `1px solid ${C.hair}`, borderRadius: 14,
                padding: '20px 22px',
              }}>
                <SectionLabel>账户余额</SectionLabel>
                <div data-testid="account-available-balance" style={{ fontFamily: C.serif, fontSize: 40, color: C.ink, lineHeight: 1, marginBottom: 16 }}>
                  ¥{Number(userStore.balance ?? 0).toFixed(2)}
                </div>
                <div data-testid="account-settling-balance" style={{ fontSize: 12, color: C.ink3, marginTop: -8, marginBottom: 16 }}>
                  入账中 ¥{Number(userStore.frozenBalance ?? 0).toFixed(2)}
                </div>
                <GhostBtn onClick={() => navigate('/user/info/withdraw')}>提现管理</GhostBtn>
              </div>

              {/* SMS notification */}
              <SmsNotificationCard />
            </div>

            {/* RIGHT: account security */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

              {/* Username */}
              <form onSubmit={handleUserNameSubmit} style={{
                background: '#fff', border: `1px solid ${C.hair}`, borderRadius: 14,
                padding: '20px 22px',
              }}>
                <SectionLabel>账户安全</SectionLabel>
                <div style={{ fontFamily: C.serif, fontSize: 20, color: C.ink, marginBottom: 14 }}>修改用户名</div>
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 12, color: C.ink3, marginBottom: 4 }}>用户名</div>
                  <input name="username" value={username || ''} onChange={handleInputChange}
                    placeholder="用户名" style={inputStyle(userNameError)} />
                  {userNameError && <div style={{ fontSize: 12, color: '#c2412e', marginTop: 4 }}>用户名应至少为6个字符，最多20个字符，只可以包含字母（无论大小写）和数字。</div>}
                </div>
                <PrimaryBtn type="submit">修改用户名</PrimaryBtn>
              </form>

              {/* Password */}
              <form onSubmit={handlePasswordSubmit} style={{
                background: '#fff', border: `1px solid ${C.hair}`, borderRadius: 14,
                padding: '20px 22px',
              }}>
                <div style={{ fontFamily: C.serif, fontSize: 20, color: C.ink, marginBottom: 14 }}>修改密码</div>
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 12, color: C.ink3, marginBottom: 4 }}>新密码</div>
                  <input type="password" name="password" value={password} onChange={handleInputChange}
                    placeholder="密码" style={inputStyle(passwordError)} />
                  {passwordError && <div style={{ fontSize: 12, color: '#c2412e', marginTop: 4 }}>密码应至少为6个字符，最多16个字符，可以包含字母（无论大小写）、数字以及特殊字符(@$!%*#?&)。</div>}
                </div>
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 12, color: C.ink3, marginBottom: 4 }}>确认密码</div>
                  <input type="password" name="confirmPassword" value={confirmPassword} onChange={handleInputChange}
                    placeholder="确认密码" style={inputStyle(confirmPasswordError)} />
                  {confirmPasswordError && <div style={{ fontSize: 12, color: '#c2412e', marginTop: 4 }}>两次输入的密码不一致！</div>}
                </div>
                <PrimaryBtn type="submit">修改密码</PrimaryBtn>
              </form>

              {/* Logout */}
              <button onClick={() => userStore.logout()} style={{
                width: '100%', padding: '12px', borderRadius: 10,
                border: `1px solid ${C.hair2}`, background: '#fff',
                color: '#A53636', fontFamily: C.sans, fontSize: 14, fontWeight: 500,
                cursor: 'pointer',
              }}>退出登录</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}));

export default withAuth(UserInfoPage);
